<template>
  <div class="PublisherDashboardTopPlugins">
    <div class="PublisherDashboardTopPlugins__header">
      <h4 class="PublisherDashboardTopPlugins__title">Top Plugins</h4>
      <publisher-dashboard-top-plugin-filter
        :filter="sortBy"
        :disabled="loading"
        @updateSortBy="updateSortBy"
      />
    </div>
    <v-row class="PublisherDashboardTopPlugins__body" v-if="!loading">
      <v-col
        v-for="plugin in topPlugins"
        :key="plugin.id"
        cols="12"
        sm="6"
        md="4"
      >
        <publisher-dashboard-plugin-card :plugin="plugin" />
      </v-col>
    </v-row>
    <div v-else class="spinner">
      <FlamePreloader :compact="true" :size="100" />
    </div>
  </div>
</template>

<script>
import { mapState } from 'vuex'
import FlamePreloader from '@/components/_Common/FlamePreloader'
import PublisherDashboardTopPluginFilter from './PublisherDashboardTopPluginFilter.vue'
import PublisherDashboardPluginCard from './PublisherDashboardPluginCard.vue'
import marketplaceAPI from '@/services/marketplace'

const legends = [
  {
    label: 'Ready for Sale',
    color: 'rgb(87, 76, 250)',
    slug: 'ready-for-sale',
  },
  { label: 'In Review', color: 'rgba(87, 76, 250, 0.5)', slug: 'in-review' },
  { label: 'Draft', color: 'rgba(87, 76, 250, 0.2)', slug: 'draft' },
]

export default {
  name: 'PublisherDashboardTopPlugins',
  components: {
    PublisherDashboardTopPluginFilter,
    PublisherDashboardPluginCard,
    FlamePreloader,
  },
  data() {
    return {
      sortBy: 'installsCount',
      data: {
        labels: ['Ready for Sale', 'In Preview', 'Draft'],
        datasets: [
          {
            data: [300, 50, 100],
            backgroundColor: [
              'rgb(87, 76, 250)',
              'rgba(87, 76, 250, 0.5)',
              'rgba(87, 76, 250, 0.2)',
            ],
            borderColor: '#1C1B2E',
            borderWidth: 4,
            borderAlign: 'inner',
            spacing: 10,
            hoverOffset: 4,
          },
        ],
      },
      options: {
        cutoutPercentage: 80,
        legend: false,
      },
      legendsData: legends,
      total: 0,
      pluginList: [],
      topPlugins: [],
      loading: false,
    }
  },
  computed: {
    ...mapState({
      currentPublisherInfo: ({ publisher_infos }) => publisher_infos.current,
    }),
  },
  async mounted() {
    await this.fetchTopPluginsList()
  },
  methods: {
    /**
     * Map sortBy value from component to API parameter
     */
    mapSortByToAPI(sortBy) {
      const mapping = {
        'installsCount': 'installs',
        'income': 'income',
        'rating': 'popularity', // API uses 'popularity' as proxy for ratings
      }
      return mapping[sortBy] || 'installs'
    },
    
    /**
     * Map top_plugins API response to legacy format
     */
    mapTopPluginToLegacyFormat(topPlugin) {
      // Map plugin_status: Handle both numeric (0-3) and string values
      // 0 = published, 1 = in-review, 2 = draft, 3 = suspended
      const numericStatusMap = {
        0: 'Ready for Sale',
        1: 'In Review',
        2: 'Draft',
        3: 'Suspended',
      }
      
      // Map string status values to legacy format
      const stringStatusMap = {
        'published': 'Ready for Sale',
        'in-review': 'In Review',
        'draft': 'Draft',
        'suspended': 'Suspended',
        'active': 'Ready for Sale',
      }
      
      let status = 'Unknown'
      if (typeof topPlugin.plugin_status === 'number') {
        status = numericStatusMap[topPlugin.plugin_status] || 'Unknown'
      } else if (typeof topPlugin.plugin_status === 'string') {
        status = stringStatusMap[topPlugin.plugin_status] || topPlugin.plugin_status
      }
      
      return {
        id: topPlugin.plugin_id,
        name: topPlugin.plugin_name,
        slug: topPlugin.plugin_slug,
        url: '', // Will be populated if needed from full plugin data
        developerContent: {
          description: topPlugin.plugin_description || '',
          featured: false, // Not in top_plugins response
        },
        developerData: {
          id: topPlugin.plugin_id,
          status: status,
          installsCount: topPlugin.total_installations || 0,
          activeInstalls: topPlugin.active_installations || 0,
          disabledInstalls: topPlugin.disabled_installations || 0,
          quarantinedInstalls: topPlugin.quarantined_installations || 0,
          rating: 0, // Not in top_plugins response yet
          income: topPlugin.income || 0,
        },
      }
    },
    
    async fetchTopPluginsList() {
      this.loading = true
      try {
        // Map component sortBy to API sort parameter
        const apiSortBy = this.mapSortByToAPI(this.sortBy)
        
        // Get installation analytics with top_plugins
        const analyticsResponse = await marketplaceAPI.getInstallationAnalytics({
          top_plugins_sort_by: apiSortBy,
          top_plugins_limit: 10, // Get top 10, then take top 3 for display
        })
        
        const analytics = analyticsResponse.analytics || analyticsResponse
        
        // Use top_plugins from analytics response
        if (analytics.top_plugins && analytics.top_plugins.length > 0) {
          // Map top_plugins to legacy format
          const mappedPlugins = analytics.top_plugins.map(this.mapTopPluginToLegacyFormat)
          
          // Take top 3 for display (already sorted by API)
          this.topPlugins = mappedPlugins.slice(0, 3)
        } else {
          // Fallback: if top_plugins not available, use by_plugin data
          console.warn('top_plugins not available, falling back to by_plugin')
          if (analytics.by_plugin && analytics.by_plugin.length > 0) {
            const mappedPlugins = analytics.by_plugin.map((item) => ({
              id: item.plugin_id,
              name: item.plugin_name,
              slug: item.plugin_slug,
              url: '',
              developerContent: {
                description: '',
                featured: false,
              },
              developerData: {
                id: item.plugin_id,
                status: 'Ready for Sale',
                installsCount: item.total_installations || 0,
                activeInstalls: item.active_installations || 0,
                rating: 0,
                income: 0,
              },
            }))
            
            // Apply client-side sorting as fallback
            this.topPlugins = this.sortPlugins(mappedPlugins).slice(0, 3)
          } else {
            this.topPlugins = []
          }
        }
      } catch (error) {
        console.error('Error fetching top plugins:', error)
        this.topPlugins = []
      }
      this.loading = false
    },
    async updateSortBy(params) {
      this.sortBy = params.sortBy
      await this.fetchTopPluginsList()
    },
    sortPlugins(plugins) {
      if (!plugins || plugins.length === 0) return []
      
      return [...plugins].sort((a, b) => {
        switch (this.sortBy) {
          case 'installsCount':
            const aInstalls = this.getInstallCount(a)
            const bInstalls = this.getInstallCount(b)
            return bInstalls - aInstalls // Descending order (highest first)
          case 'income':
            const aIncome = this.getIncome(a)
            const bIncome = this.getIncome(b)
            return bIncome - aIncome // Descending order (highest first)
          case 'rating':
            const aRating = this.getRating(a)
            const bRating = this.getRating(b)
            return bRating - aRating // Descending order (highest first)
          default:
            return 0
        }
      })
    },
    getInstallCount(plugin) {
      // Handle different possible data structures
      if (plugin.developerData?.installsCount !== undefined) {
        return parseInt(plugin.developerData.installsCount) || 0
      }
      if (plugin.installsCount !== undefined) {
        return parseInt(plugin.installsCount) || 0
      }
      if (plugin.installCount !== undefined) {
        return parseInt(plugin.installCount) || 0
      }
      return 0
    },
    getIncome(plugin) {
      // Handle income calculation if needed
      if (plugin.developerData?.income !== undefined) {
        return parseFloat(plugin.developerData.income) || 0
      }
      return 0
    },
    getRating(plugin) {
      // Handle rating calculation if needed
      if (plugin.developerData?.rating !== undefined) {
        return parseFloat(plugin.developerData.rating) || 0
      }
      return 0
    },
  },
}
</script>

<style lang="scss">
$style: PublisherDashboardTopPlugins;
.#{$style} {
  margin-bottom: 50px;
  &__header {
    display: flex;
    flex-direction: row;
    width: 100%;
    justify-content: space-between;
  }
  &__title {
    color: $white;
    font-weight: 500;
    font-size: $H20;
  }
  &__body {
    margin-top: 10px;
  }
  @media screen and (max-width: 600px) {
    &__header {
      flex-direction: column;
      align-items: flex-start;
      gap: 8px;
    }
  }
}
.w-full {
  width: 100%;
}
</style>
